import styles from './ArrowButton.module.scss';

import cx from 'classnames';
import React, { ButtonHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomArrowProps } from 'react-slick';

import { CarouselType } from 'api/contentful/contentTypes.cloverdotcom';
import { MaterialIcon } from 'components/MaterialIcon/MaterialIcon';
import { CircleArrowButton } from 'components/CircleArrowButton/CircleArrowButton';
import { useBreakpoints } from 'hooks/useBreakpoints';

export enum ArrowType {
  PREV = 'prevArrow',
  NEXT = 'nextArrow',
}

export interface IArrowButton extends CustomArrowProps {
  arrowDirection: ArrowType;
  // react slick doesn't merge className
  classNameOverride?: string;
  carouselType?: CarouselType;
  infinite?: boolean;
  isCircleArrow?: boolean;
  variant?: 'left' | 'right';
  isCircleArrowButton?: boolean;
}

export const ArrowButton: React.FC<IArrowButton & ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className = '',
  style,
  arrowDirection,
  slideCount,
  currentSlide,
  classNameOverride,
  carouselType,
  isCircleArrow,
  infinite,
  variant,
  ...props
}) => {
  const { t } = useTranslation();
  const { browser } = useBreakpoints();

  const isDesktop = browser.is.desktop && !browser.greaterThan.desktop;

  // This function is only relevent for desktop screen-sizes
  const hideCarrot = (isDesktop: boolean, slideCount: number, currentSlide: number) => {
    const isFinalSlideVisible = currentSlide + 3 === slideCount && arrowDirection === ArrowType.NEXT;
    return isDesktop && !infinite && isFinalSlideVisible;
  };

  const isTypeSquareV2Translation = arrowDirection === ArrowType.PREV ? t('previous') : t('next');

  return (
    <>
      {isCircleArrow ? (
        <CircleArrowButton
          className={cx(className, classNameOverride, styles.arrowButton, {
            [styles.arrowButtonHide]: hideCarrot(isDesktop, slideCount!, currentSlide!),
            [styles.arrowButtonSquareV2Left]: arrowDirection === ArrowType.PREV,
            [styles.arrowButtonSquareV2Right]: arrowDirection === ArrowType.NEXT,
            [styles.arrowButtonProduct]: carouselType === CarouselType.PRODUCT,
            [styles.arrowButtonFeature]: carouselType === CarouselType.FEATURE,
            [styles.arrowButtonSquare]: carouselType === CarouselType.SQUARE,
            [styles.arrowButtonHero]: carouselType === CarouselType.HERO,
          })}
          variant={variant}
          {...props}
        />
      ) : (
        <button
          title={isTypeSquareV2Translation}
          aria-label={isTypeSquareV2Translation}
          aria-disabled={className.indexOf('slick-disabled') > -1 ? true : undefined}
          className={cx(className, classNameOverride, styles.arrowButton, {
            [styles.arrowButtonHide]: hideCarrot(isDesktop, slideCount!, currentSlide!),
            [styles.arrowButtonLeft]: arrowDirection === ArrowType.PREV,
            [styles.arrowButtonRight]: arrowDirection === ArrowType.NEXT,
            [styles.arrowButtonProduct]: carouselType === CarouselType.PRODUCT,
            [styles.arrowButtonFeature]: carouselType === CarouselType.FEATURE,
            [styles.arrowButtonSquare]: carouselType === CarouselType.SQUARE,
            [styles.arrowButtonHero]: carouselType === CarouselType.HERO,
          })}
          {...props}
        >
          <MaterialIcon
            icon={cx({
              chevron_left: arrowDirection === ArrowType.PREV,
              chevron_right: arrowDirection === ArrowType.NEXT,
            })}
          />
        </button>
      )}
    </>
  );
};
